import React, { useMemo } from 'react';

import CustomSizePicker from 'components/shared/CustomSizePicker';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import {
  selectCatalogsLoaded,
  selectCustomPriceLoading,
  selectOptionsLoaded,
  selectSubcatalogsLoaded,
} from 'selectors/loading_states';
import { selectProductPrice } from 'selectors/prices';

import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';
import products from 'domains/products';
import ui from 'domains/ui';

import type { CustomSizePayload } from 'domains/canvases';
import type { CustomSizeProductCustomOptions, ProductTemplateAPIData } from 'types/catalog_types';
import type { Dimension } from 'types/common_types';

type PriceToolCustomSizePickerContainerProps = {
  customProduct: ProductTemplateAPIData;
  priceToolDimension: Dimension;
  setPriceToolDimension: (dimension: Dimension) => void;
};

function PriceToolCustomSizePickerContainer(props: PriceToolCustomSizePickerContainerProps) {
  const { customProduct, priceToolDimension, setPriceToolDimension } = props;

  const dispatch = useAppDispatch();
  const catalogsLoaded = useAppSelector(selectCatalogsLoaded);
  const subcatalogsLoaded = useAppSelector(selectSubcatalogsLoaded);
  const optionsLoaded = useAppSelector(selectOptionsLoaded);
  const customPriceLoading = useAppSelector(selectCustomPriceLoading);
  const customSizeErrorMessage = useAppSelector(ui.selectors.customSizeErrors);
  const price = useAppSelector(selectProductPrice);
  const allProducts = useAppSelector(activeEditor.selectors.products);

  const isLoading = useMemo(() => {
    return !catalogsLoaded || !subcatalogsLoaded || !optionsLoaded;
  }, [catalogsLoaded, subcatalogsLoaded, optionsLoaded]);

  function setCustomSize(payload: CustomSizePayload) {
    const update = { ...priceToolDimension, ...payload };

    setPriceToolDimension(update);

    const standardMatch = products.utils.getProductIdFromDimensions(
      update.width,
      update.height,
      allProducts
    );

    if (standardMatch) {
      dispatch(canvases.actions.changeActiveCanvasConfig.product(standardMatch));
    } else {
      dispatch(
        canvases.actions.changeActiveCanvasConfig.priceToolSize({
          product: customProduct.id,
          ...update,
        })
      );
    }
  }

  // Price tool needs to use the custom options from whatever the current subcatalog's
  // custom product is
  const priceToolCustomOptions = useMemo(() => {
    return allProducts.find((p) => !!p.custom_options)?.custom_options;
  }, [products]);

  if (!priceToolCustomOptions) return null;

  return (
    <CustomSizePicker
      customPriceLoading={customPriceLoading}
      isLoading={isLoading}
      setCustomSize={setCustomSize}
      customWidth={priceToolDimension.width}
      customHeight={priceToolDimension.height}
      activeProductCustomOptions={priceToolCustomOptions as CustomSizeProductCustomOptions}
      customPrice={price.toString()}
      showPrice={false}
      errorMessage={customSizeErrorMessage}
    />
  );
}

export default PriceToolCustomSizePickerContainer;
